import React from "react";
import { safeFormatDate } from "../../util/dateUtils";
import { Empty, Tag } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  ThunderboltFilled,
  ClockCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import "./Approvals.css";

/**
 * The per-level timeline for a module's detail drawer, built from
 * `approval_progress.levels`. SKIPPED, BYPASSED and NOT_REQUIRED read
 * differently on purpose - an employee seeing "skipped" where a senior
 * overrode, or "pending" where the chain is already dead, is being told the
 * wrong thing about who decided their request
 * (docs/approval-workflow/10-settings-ui.md §6).
 */

const STATUS_DISPLAY = {
  APPROVED: { icon: <CheckCircleFilled />, colour: "green", caption: () => "Approved" },
  REJECTED: { icon: <CloseCircleFilled />, colour: "red", caption: () => "Rejected" },
  SKIPPED: { icon: <MinusCircleFilled />, colour: "grey", caption: () => "Skipped: no one holds this role" },
  BYPASSED: {
    icon: <ThunderboltFilled />,
    colour: "purple",
    // The bypass itself never carries its own approve/reject outcome - it's
    // whatever the override actor decided at the stage they actually acted
    // on, reflected here by the request's overall final status.
    caption: (level, overallStatus) =>
      overallStatus === "REJECTED"
        ? `Skipped: ${level.bypassedBy || "an override"} rejected the request directly`
        : `Approved directly by ${level.bypassedBy || "an override"}`,
  },
  // The chain ended before this level was reached. Nobody overrode anyone and
  // nobody is waiting - saying "Pending" here would tell the applicant this
  // level still owes them an answer.
  NOT_REQUIRED: {
    icon: <StopOutlined />,
    colour: "grey",
    caption: (level, overallStatus) =>
      overallStatus === "REJECTED"
        ? "No action needed: the request was rejected before this level"
        : overallStatus === "WITHDRAWN"
        ? "No action needed: the applicant withdrew the request"
        : "No action needed: the request was closed before this level",
  },
  PENDING: { icon: <ClockCircleOutlined />, colour: "blue", caption: () => "Pending" },
};

/** The closing line under the levels, for a request that is no longer open. */
const CLOSURE_DISPLAY = {
  APPROVED: { colour: "green", label: "Approved" },
  REJECTED: { colour: "red", label: "Rejected" },
  CANCELLED: { colour: "red", label: "Cancelled" },
  WITHDRAWN: { colour: "orange", label: "Withdrawn by applicant" },
};

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return null;
  }
};

const formatDay = (value) => {
  if (!value) return null;
  try {
    return safeFormatDate(value, undefined, null);
  } catch (e) {
    return null;
  }
};

const ApprovalTimeline = ({ progress }) => {
  if (!progress || !progress.levels?.length) {
    return <Empty description="No approval chain on this request" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const closure = progress.closure;
  const closureDisplay = closure ? CLOSURE_DISPLAY[closure.status] : null;

  return (
    <div className="aw-timeline">
      {progress.synthetic && (
        <div className="aw-timeline-note">
          Recorded before multi-level approvals were enabled: one approver, no chain.
        </div>
      )}

      {progress.levels.map((level) => {
        const display = STATUS_DISPLAY[level.status] || STATUS_DISPLAY.PENDING;
        const actedAt = formatDate(level.actedAt);
        const lastWorkingDate = formatDay(level.meta?.lastWorkingDate);

        return (
          <div key={level.level} className="aw-timeline-row">
            <div className={`aw-timeline-icon aw-timeline-icon--${display.colour}`}>{display.icon}</div>
            <div className="aw-timeline-body">
              <div className="aw-timeline-head">
                <span className="aw-timeline-level">
                  Level {level.level} · {level.name}
                </span>
                {actedAt && <span className="aw-timeline-time">{actedAt}</span>}
              </div>
              <div className="aw-timeline-caption">
                {level.status === "PENDING"
                  ? display.caption()
                  : level.actedBy
                  ? `${display.caption(level, progress.status)} · ${level.actedBy}`
                  : display.caption(level, progress.status)}
                {level.basis === "OVERRIDE" && level.status !== "BYPASSED" && (
                  <span className="aw-timeline-override-tag"> · via override</span>
                )}
              </div>
              {/* What this level actually signed off on. A later level moving
                  the date does not rewrite what this one agreed to. */}
              {lastWorkingDate && (
                <div className="aw-timeline-meta">Last working day: {lastWorkingDate}</div>
              )}
              {level.comment && <div className="aw-timeline-comment">&ldquo;{level.comment}&rdquo;</div>}
            </div>
          </div>
        );
      })}

      {closureDisplay && (
        <div className="aw-timeline-closure">
          <Tag color={closureDisplay.colour}>{closureDisplay.label}</Tag>
          <span className="aw-timeline-closure-text">
            {closure.by ? `by ${closure.by}` : ""}
            {closure.at ? ` · ${formatDate(closure.at)}` : ""}
          </span>
          {closure.reason && (
            <div className="aw-timeline-comment">&ldquo;{closure.reason}&rdquo;</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalTimeline;
